import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchJobenquiries,
    createJobenquiry,
    updateJobenquiry,
    deleteJobenquiry,
    downloadJobenquiry,
    setPagination,
    setSort,
    searchJobenquiries,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,

    changeClassicFilter
} from '../../store/modules/jobenquiry/jobenquiryActions';
import JobenquiryForm from '../../components/pageModule/Jobenquiries/JobenquiryForm';
import jobenquiryService from '../../services/jobenquiryService';

const MySwal = withReactContent(Swal);

const Jobenquiries = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const jobenquiryState = useSelector(state => state.jobenquiry);
    const dispatch = useDispatch();
    const [stockIssueRows, setStockIssueRows] = useState([]);
    const [stockIssueLoading, setStockIssueLoading] = useState(false);

    const getTextValue = (value) => {
        if (!value) {
            return '-';
        }

        if (typeof value === 'object') {
            return value.en ?? Object.values(value)[0] ?? '-';
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object') {
                    return parsed.en ?? Object.values(parsed)[0] ?? value;
                }
            } catch (error) {
                return value;
            }
            return value;
        }

        return String(value);
    };

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createJobenquiry(formData));
            toast.success('Jobenquiry created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating jobenquiry', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateJobenquiry(id, formData));
            toast.success('Jobenquiry updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating jobenquiry', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteJobenquiry(id));
                toast.success('Jobenquiry deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting jobenquiry', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleDownload = async (type) => {
        try {
            const response = await dispatch(downloadJobenquiry(type));
            window.open(response.url, '_blank')
            return response;
        } catch (error) {
            toast.error(error.message || 'Error downloading jobenquiries', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const onItemRowClicked = async (row) => {
        if (!row?.id) {
            setStockIssueRows([]);
            return;
        }

        try {
            setStockIssueLoading(true);
            const response = await jobenquiryService.getOne(row.id);
            setStockIssueRows(response?.data ?? []);
        } catch (error) {
            setStockIssueRows([]);
            toast.error(error.message || 'Error loading stock issues', {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setStockIssueLoading(false);
        }
    };

    const columns = [
        {
            name: 'JobOrderNo',
            selector: row => row.job_order_no ?? '-',
            resizable: true,
        },
        {
            name: 'JobOrderDate',
            selector: row => row.job_order_date ?? '-',
            resizable: true,
        },
        {
            name: 'CustomerLPONo',
            selector: row => row.customer_lpo_no ?? '-',
            resizable: true,
        },
        {
            name: 'CustomerCode',
            selector: row => row.customer_code ?? '-',
            resizable: true,
        },
        {
            name: 'CustomerName',
            selector: row => row.customer_name?.en ?? row.customer_name ?? '-',
            minWidth: '200px',
            resizable: true,
        },
        {
            name: 'CustomerPhone',
            selector: row => row.customer_phone ?? '-',
            resizable: true,
        },
        {
            name: 'NameOfEmployee',
            selector: row => row.employee_name ?? '-',
            resizable: true,
        },
        {
            name: 'DueDate',
            selector: row => row.due_date ?? '-',
        },
        {
            name: 'TrialDate',
            selector: row => row.trial_date ?? '-',
        },
        {
            name: 'ItemCode',
            selector: row => row.item_code ?? '-',
        },
        {
            name: 'Description',
            selector: row => row.item_name?.en ?? row.item_name ?? row.description ?? '-',
            grow: 2,
        },
        {
            name: 'Quantity',
            selector: row => row.quantity ?? '-',
        },
        {
            name: 'UnitPrice',
            selector: row => row.unit_price ?? '-',
        },
        {
            name: 'LineDiscountPerc',
            selector: row => row.line_discount_perc ?? '-',
        },
        {
            name: 'LineAmount',
            selector: row => row.line_amount ?? '-',
        },
        {
            name: 'Status',
            selector: row => row.status ?? '-',
        },
        {
            name: 'DeliveryNoteNo',
            selector: row => (!row.is_lpo) ? '' : row.delivery_note_no ?? '-',
            resizable: true,
        },
        {
            name: 'DNDate',
            selector: row => (!row.is_lpo) ? '' : row.dn_date ?? '-',
            resizable: true,
        },
        {
            name: 'InvoiceNo',
            selector: row => row.invoice_no ?? '-',
            resizable: true,
        },
        {
            name: 'InvoiceDate',
            selector: row => row.invoice_date ?? '-',
            resizable: true,
        },
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => {
            setStockIssueRows([]);
            return dispatch(fetchJobenquiries(queryFilter, activeFilter, shouldUseFilter, externalCondition));
        },
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        download: handleDownload,
        search: (search) => dispatch(searchJobenquiries(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),

        onClassicFilterChange: (name, value) => dispatch(changeClassicFilter(name, value)),
    };

    const defaultAvailableActions = [];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title=""
                moduleName="Jobenquiry"
                moduleKey="jobenquiries"
                state={jobenquiryState}
                actions={actions}
                FormComponent={JobenquiryForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onItemRowClicked}
                clickedRowHighlight={true}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
                enableResize
                customComponents={
                    {
                        after: () => (
                            <div className="mt-3">
                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Job Order No</th>
                                                <th>Voucher Date</th>
                                                <th>FG Code</th>
                                                <th>FG Name</th>
                                                <th>Issued To</th>
                                                <th>Issued To ID</th>
                                                <th>Issued To Name</th>
                                                <th>FG Quantity</th>
                                                <th>Quantity Produced</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stockIssueLoading ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-3">Loading...</td>
                                                </tr>
                                            ) : stockIssueRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-3">Click a row to load stock issues.</td>
                                                </tr>
                                            ) : (
                                                stockIssueRows.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.job_order_no ?? '-'}</td>
                                                        <td>{item.voucher_date ?? '-'}</td>
                                                        <td>{item.fg_code ?? '-'}</td>
                                                        <td>{getTextValue(item.fg_name)}</td>
                                                        <td>{item.issued_to ?? '-'}</td>
                                                        <td>{item.issued_to_id ?? '-'}</td>
                                                        <td>{getTextValue(item.issued_to_name)}</td>
                                                        <td>{item.fg_qty ?? '-'}</td>
                                                        <td>{item.quantity_produced ?? '-'}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )
                    }
                }
            />
        </>
    );
};

export default Jobenquiries; 